import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Eye } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { StaffInfo } from '../../types';
import { useEstates } from '../../lib/useEstates';
import { Spinner, EmptyState, Modal, Alert, ConfirmDialog, inputClass, labelClass, primaryBtn, secondaryBtn, Avatar } from '../../components/ui';
import StaffDetailModal from '../../components/StaffDetailModal';

export const STAFF_ROLES = [
  'facility manager',
  'security',
  'electrician',
  'plumber',
  'pool cleaner',
  'generator technician',
  'lift technician',
];

export const ROLE_OPTIONS: StaffInfo['role'][] = ['admin', 'oversight', 'manager', 'staff', 'account'];

interface FormState {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: StaffInfo['role'];
  role_title: string;
  estate: string;
  is_active: boolean;
}

const emptyForm: FormState = { name: '', email: '', phone: '', password: '', role: 'staff', role_title: '', estate: '', is_active: true };

export default function Staff() {
  const { estateNames } = useEstates();
  const [users, setUsers] = useState<StaffInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState<StaffInfo | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState<StaffInfo | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<StaffInfo[]>(`/auth/users${buildQuery({ role: roleFilter })}`)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [roleFilter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/users', form);
      setCreateOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/auth/users/${editUser.id}`, editForm);
      setEditUser(null);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u: StaffInfo) => {
    setError('');
    setEditForm({
      name: u.name,
      email: u.email,
      phone: u.phone || '',
      password: '',
      role: u.role,
      role_title: u.role_title || '',
      estate: u.estate || '',
      is_active: u.is_active !== false,
    });
    setEditUser(u);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/auth/users/${deleteId}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  const roleFields = (f: FormState, set: (f: FormState) => void) => (
    <>
      <div>
        <label className={labelClass}>Role *</label>
        <select
          className={inputClass}
          value={f.role}
          onChange={(e) => set({ ...f, role: e.target.value as StaffInfo['role'], role_title: '' })}
        >
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
      </div>
      {f.role !== 'admin' && (
        <>
          {f.role === 'staff' ? (
            <div>
              <label className={labelClass}>Staff Role *</label>
              <select className={inputClass} value={f.role_title} onChange={(e) => set({ ...f, role_title: e.target.value })}>
                <option value="">Select a staff role...</option>
                {STAFF_ROLES.map((s) => <option key={s} value={s}>{s.replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className={labelClass}>Role Title (e.g. Sales Agent)</label>
              <input className={inputClass} value={f.role_title} onChange={(e) => set({ ...f, role_title: e.target.value })} />
            </div>
          )}
          <div>
            <label className={labelClass}>Estate / Facility Worked In</label>
            <select className={inputClass} value={f.estate} onChange={(e) => set({ ...f, estate: e.target.value })}>
              <option value="">Select estate...</option>
              {estateNames.map((en) => <option key={en} value={en}>{en}</option>)}
            </select>
          </div>
        </>
      )}
    </>
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Staff & Users</h1>
        <button onClick={() => { setError(''); setCreateOpen(true); }} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Add User
        </button>
      </div>

      <div className="mb-4">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading users..." />
      ) : users.length === 0 ? (
        <EmptyState message="No users found." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Role Title</th>
                <th className="px-4 py-3">Estate</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setDetail(u)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Avatar src={u.image_url} name={u.name} className="w-8 h-8 mr-3" />
                      <span className="font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3 text-gray-500">{u.phone || '—'}</td>
                  <td className="px-4 py-3 capitalize">{u.role}</td>
                  <td className="px-4 py-3 text-gray-500">{u.role_title || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{u.estate || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setDetail(u)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-primary"
                        title="View user"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEdit(u)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-primary"
                        title="Edit user"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteId(u.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Add User">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelClass}>Full Name *</label>
            <input className={inputClass} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" className={inputClass} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Password * (min 8 characters)</label>
            <input type="password" className={inputClass} required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          {roleFields(form, setForm)}
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setCreateOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Creating...' : 'Create User'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit User">
        {error && <Alert>{error}</Alert>}
        {editUser && (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className={labelClass}>Full Name *</label>
              <input className={inputClass} required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" className={inputClass} disabled value={editForm.email} />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input className={inputClass} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>New Password (leave blank to keep current)</label>
              <input type="password" className={inputClass} minLength={8} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
            </div>
            {roleFields(editForm, setEditForm)}
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editForm.is_active}
                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary"
              />
              <span className="text-sm text-gray-700">Active account</span>
            </label>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => setEditUser(null)} className={secondaryBtn}>Cancel</button>
              <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
      />

      <StaffDetailModal staff={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
